import { $user_config } from "@/configs/user";
import { $cookie } from "@/cookie";
import { $event } from "@/core/event";
import { SdkPlugin } from "@/core/plugin";
import { $event_subscribe } from "@/core/subscribe";
import { Provide } from "@/di/provide";
import { PageService } from "@/services/page";
import { $config } from "@/settings";
import { getDomain } from "@/utils/domain";

import type { PageEvent } from "@/core/subscribe";
import type { PageBehaviorParams } from "@/interface/api";
import type { SdkContext } from "@/interface/sdk";

export type TrackerType = "page_load" | "page_close";

/**
 * 埋点与上报类
 */
export class TrackerPlugin extends SdkPlugin {
  static pluginName = "Tracker" as const;

  @Provide(PageService)
  xhr!: PageService;

  click_btn = "";
  link_source = "";
  trackConfig: { autoPage?: boolean } = {}; // Added this line to define trackConfig
  appId = "";

  init(ctx: SdkContext): void {
    this.appId = ctx.options?.appId ?? "";
    const options = ctx.options?.track;
    if (options !== undefined) {
      this.click_btn = $cookie.get("click_btn") || "-";
      this.link_source = $cookie.get("link_source") || "";
      this.trackConfig = options; // Store options for userEnd
    }
    $event_subscribe.on("page_close").subscribe(() => {
      if (options !== undefined) {
        this.removeCookie();
        this.trackPageClose();
      }
    });
    $event_subscribe.on("track").subscribe((s) => {
      void this.trackCustom(s);
    });
  }

  userEnd(context: SdkContext): void | Promise<void> {
    if (context.options != undefined) {
      if (context.options.track != undefined) {
        if (context.options.track.autoPage) {
          void this.track<
            Omit<
              PageBehaviorParams,
              | "behavior"
              | "click_btn"
              | "link_source"
              | "anonymous_id"
              | "app_id"
            >
          >("page_load", { request_id: $event.getId() });
        }
      } else {
        if (context.options.appId != "" && context.options.appId != undefined) {
          this.trackExposure();
        }
      }
    }
  }

  private removeCookie(): void {
    const href = window.location.href;
    const domain = getDomain(href);
    $cookie.remove("click_btn", { domain: domain });
    $cookie.remove("link_source", { domain: domain });
  }

  /** 异步上报事件 */
  public async track<T>(event: TrackerType, _data?: T): Promise<void> {
    switch (event) {
      case "page_close":
        this.trackPageClose();
        break;
      case "page_load":
        this.trackPageLoad();
        break;
    }
  }

  private trackPageLoad(): void {
    void this.xhr.behavior({
      app_id: this.appId,
      click_btn: this.click_btn,
      link_source: this.link_source,
      behavior: "join",
      request_id: $event.getId(),
    });
  }

  private trackExposure(): void {
    void this.xhr.behavior({
      app_id: this.appId,
      click_btn: this.click_btn,
      link_source: this.link_source,
      behavior: "exposure",
      request_id: $event.getId(),
    });
  }

  trackCustom(s: PageEvent["track"]): Promise<boolean> {
    const json: { [key: string]: PageBehaviorParams["behavior"] } = {
      form: "clue_form",
      tel: "clue_tel",
      human: "clue_human",
    };
    return new Promise<boolean>((resolve, reject) => {
      this.xhr
        .behavior({
          app_id: this.appId,
          click_btn: this.click_btn,
          link_source: this.link_source,
          behavior: json[s],
          request_id: $event.getId(),
        })
        .then(
          () => {
            resolve(true);
          },
          () => {
            reject(false);
          },
        );
    });
  }

  private trackPageClose(): void {
    const info: PageBehaviorParams = {
      app_id: this.appId,
      click_btn: this.click_btn,
      link_source: this.link_source,
      behavior: "leave",
      request_id: $event.getId(),
      anonymous_id: $user_config.get("cookieId"),
    };
    // const blob = new Blob([JSON.stringify(info)], {
    //   type: "application/x-www-form-urlencoded",
    // });
    // console.log(blob);
    // const formData = new FormData();
    // formData.append("data", JSON.stringify(info));
    const params = new URLSearchParams();
    Object.entries(info).forEach(([key, value]) => {
      params.append(key, String(value));
    });
    if (typeof window.navigator.sendBeacon == "function") {
      const res = window.navigator.sendBeacon(
        `${$config.apiUrl}/api/interaction/behavior`,
        params,
      );
      if (!res) {
        void this.xhr.behavior(info);
      }
    } else {
      void this.xhr.behavior(info);
    }
  }
}

/**
 * 模块扩展：将 TrackerPlugin 注册到插件注册表
 *
 * 使用 typeof 自动从 pluginName 推断类型，确保插件名称只需定义一次。
 */
declare module "@/interface/plugin-registry" {
  interface PluginRegistry {
    [TrackerPlugin.pluginName]: TrackerPlugin;
  }
}
